// Colour maths for brand tokens: sRGB <-> OKLab/OKLCH and WCAG 2.1 contrast. A line-for-line port of
// tools/design-tokens/generate_brand_tokens.py, so both give the same hex for the same input (golden fixtures).

type Rgb = [number, number, number];

/** Python's round(): halves go to the even neighbour. */
function roundHalfEven(x: number): number {
  const r = Math.round(x);
  return Math.abs(x % 1) === 0.5 && r % 2 !== 0 ? r - 1 : r;
}

export function hexToRgb(hex: string): Rgb {
  let h = hex.replace(/^#/, '');
  if (h.length === 3) {
    h = [...h].map((c) => c + c).join('');
  }
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255) as Rgb;
}

export function rgbToHex(rgb: Rgb): string {
  return (
    '#' +
    rgb
      .map((c) => roundHalfEven(Math.max(0, Math.min(1, c)) * 255).toString(16).toUpperCase().padStart(2, '0'))
      .join('')
  );
}

const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const fromLinear = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);

export function rgbToOklch(rgb: Rgb): [number, number, number] {
  const [r, g, b] = rgb.map(toLinear) as Rgb;
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
  const [l_, m_, s_] = [l, m, s].map((v) => Math.sign(v) * Math.abs(v) ** (1 / 3)) as Rgb;
  const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
  const a = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
  const bb = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;
  const hue = (((Math.atan2(bb, a) * 180) / Math.PI) % 360 + 360) % 360;
  return [L, Math.hypot(a, bb), hue];
}

function oklchToLinear(L: number, C: number, H: number): Rgb {
  const a = C * Math.cos((H * Math.PI) / 180);
  const b = C * Math.sin((H * Math.PI) / 180);
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;
  const [l, m, s] = [l_ ** 3, m_ ** 3, s_ ** 3];
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

const inGamut = (lin: Rgb) => lin.every((c) => c >= -1e-4 && c <= 1 + 1e-4);

/** The colour at this lightness and hue, with chroma reduced until it fits sRGB. */
export function oklchToHex(L: number, C: number, H: number): string {
  let chroma = C;
  if (!inGamut(oklchToLinear(L, C, H))) {
    let lo = 0;
    let hi = C;
    for (let i = 0; i < 30; i++) {
      const mid = (lo + hi) / 2;
      if (inGamut(oklchToLinear(L, mid, H))) {
        lo = mid;
      } else {
        hi = mid;
      }
    }
    chroma = lo;
  }
  return rgbToHex(oklchToLinear(L, chroma, H).map(fromLinear) as Rgb);
}

export function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(toLinear) as Rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2.1 contrast ratio of two colours, from 1 to 21. */
export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}
